type Currency = {
  code: string;
  digits: number;
  minCharge: number;
  name: string;
};

// Note: These currencies are extracted by getting Stripe compatible currencies with:
//
// curl https://api.stripe.com/v1/country_specs\?limit\=100 -u $STRIPE_TEST_KEY: -G \
//   | jq '.data|.[]|.supported_payment_currencies|.[]' \
//   | sort \
//   | uniq
//
// And then populating in the names from ISO 4217:
//
// Digit counts are also added. Stripe is treated as the source-of-truth for that number, even though their
// numbers are often at odds with ISO 4217. Notes:
//
//  - CVE had a subunit (centavo), that has been discontinued. The ISO reflects this, but stripe sticks
//    with 2 digits, since the currency is still commonly formatted as "1$00". ¯\_(ツ)_/¯
//  - ISK had a subunit (eyrir) that was obsoleted in 2003, but Stripe sticks with 2 digits instead of
//    the ISO's 0.
//  - MGA is strange, since its smallest denomination is a 1/5th piece (the Iraimbilanja), but is
//    represented as a decimal, so the currency goes "1.3, 1.4, 2.0, ...". Stripe dodges this strangeness
//    by ignoring that minimum unit, and so do we, since it has so little value.
//  - UGX had a subunit (cent) that was discontinued in 2013. Stripe still counts it, tho, even though
//    that cent is work 1/350000th of a penny.
export const stripeCurrencyList: Array<Currency> = [
  /*
   * ----------------------------------------------------------------------------
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   *
   * This const is duplicated to @packages/systems/commerce/constants/stripeCurrencyList.js
   * to avoid bundling this entire SharedConfig file in the webflow.js published
   * site bundle. Once we have support for bundling for the Dashboard (this
   * config is injected in to window.SharedConfig for angular), we should
   * be able to delete this const and import directly from the package above.
   *
   * In the meantime, if you make changes to this const, please make sure to
   * update in the other location as well.
   *
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * ----------------------------------------------------------------------------
   */

  {code: 'AED', digits: 2, minCharge: 0, name: 'United Arab Emirates Dirham'},
  {code: 'AFN', digits: 2, minCharge: 0, name: 'Afghanistan Afghani'},
  {code: 'ALL', digits: 2, minCharge: 0, name: 'Albanian Lek'},
  {code: 'AMD', digits: 2, minCharge: 0, name: 'Armenia Dram'},
  {
    code: 'ANG',
    digits: 2,
    minCharge: 0,
    name: 'Netherlands Antillean Gulden',
  },
  {code: 'AOA', digits: 2, minCharge: 0, name: 'Angola Kwanza'},
  {code: 'ARS', digits: 2, minCharge: 0, name: 'Argentine Peso'},
  {code: 'AUD', digits: 2, minCharge: 50, name: 'Australian Dollar'},
  {code: 'AWG', digits: 2, minCharge: 0, name: 'Aruban Florin'},
  {code: 'AZN', digits: 2, minCharge: 0, name: 'Azerbaijan Manat'},
  {
    code: 'BAM',
    digits: 2,
    minCharge: 0,
    name: 'Bosnia and Herzegovina Convertible Marka',
  },
  {code: 'BBD', digits: 2, minCharge: 0, name: 'Barbadian Dollar'},
  {code: 'BDT', digits: 2, minCharge: 0, name: 'Bangladeshi Taka'},
  {code: 'BGN', digits: 2, minCharge: 0, name: 'Bulgaria Lev'},
  {code: 'BIF', digits: 0, minCharge: 0, name: 'Burundian Franc'},
  {code: 'BMD', digits: 2, minCharge: 0, name: 'Bermudian Dollar'},
  {code: 'BND', digits: 2, minCharge: 0, name: 'Brunei Dollar'},
  {code: 'BOB', digits: 2, minCharge: 0, name: 'Bolivian Boliviano'},
  {code: 'BRL', digits: 2, minCharge: 50, name: 'Brazilian Real'},
  {code: 'BSD', digits: 2, minCharge: 0, name: 'Bahamian Dollar'},
  {code: 'BWP', digits: 2, minCharge: 0, name: 'Botswana Pula'},
  {code: 'BZD', digits: 2, minCharge: 0, name: 'Belize Dollar'},
  {code: 'CAD', digits: 2, minCharge: 50, name: 'Canadian Dollar'},
  {code: 'CDF', digits: 2, minCharge: 0, name: 'Congo/Kinshasa Franc'},
  {code: 'CHF', digits: 2, minCharge: 50, name: 'Swiss Franc'},
  {code: 'CLP', digits: 0, minCharge: 0, name: 'Chilean Peso'},
  {code: 'CNY', digits: 2, minCharge: 0, name: 'Chinese Renminbi Yuan'},
  {code: 'COP', digits: 2, minCharge: 0, name: 'Colombian Peso'},
  {code: 'CRC', digits: 2, minCharge: 0, name: 'Costa Rican Colón'},
  {code: 'CVE', digits: 2, minCharge: 0, name: 'Cape Verdean Escudo'}, // See above.
  {code: 'CZK', digits: 2, minCharge: 0, name: 'Czech Koruna'},
  {code: 'DJF', digits: 0, minCharge: 0, name: 'Djiboutian Franc'},
  {code: 'DKK', digits: 2, minCharge: 250, name: 'Danish Krone'},
  {code: 'DOP', digits: 2, minCharge: 0, name: 'Dominican Peso'},
  {code: 'DZD', digits: 2, minCharge: 0, name: 'Algerian Dinar'},
  {code: 'EGP', digits: 2, minCharge: 0, name: 'Egyptian Pound'},
  {code: 'ETB', digits: 2, minCharge: 0, name: 'Ethiopian Birr'},
  {code: 'EUR', digits: 2, minCharge: 50, name: 'Euro'},
  {code: 'FJD', digits: 2, minCharge: 0, name: 'Fijian Dollar'},
  {code: 'FKP', digits: 2, minCharge: 0, name: 'Falkland Islands Pound'},
  {code: 'GBP', digits: 2, minCharge: 30, name: 'British Pound'},
  {code: 'GEL', digits: 2, minCharge: 0, name: 'Georgia Lari'},
  {code: 'GIP', digits: 2, minCharge: 0, name: 'Gibraltar Pound'},
  {code: 'GMD', digits: 2, minCharge: 0, name: 'Gambian Dalasi'},
  {code: 'GNF', digits: 0, minCharge: 0, name: 'Guinean Franc'},
  {code: 'GTQ', digits: 2, minCharge: 0, name: 'Guatemalan Quetzal'},
  {code: 'GYD', digits: 2, minCharge: 0, name: 'Guyanese Dollar'},
  {code: 'HKD', digits: 2, minCharge: 400, name: 'Hong Kong Dollar'},
  {code: 'HNL', digits: 2, minCharge: 0, name: 'Honduran Lempira'},
  {code: 'HRK', digits: 2, minCharge: 0, name: 'Croatian Kuna'},
  {code: 'HTG', digits: 2, minCharge: 0, name: 'Haitian Gourde'},
  {code: 'HUF', digits: 2, minCharge: 0, name: 'Hungarian Forint'},
  {code: 'IDR', digits: 2, minCharge: 0, name: 'Indonesian Rupiah'},
  {code: 'ILS', digits: 2, minCharge: 0, name: 'Israeli New Sheqel'},
  {code: 'INR', digits: 2, minCharge: 50, name: 'Indian Rupee'},
  {code: 'ISK', digits: 2, minCharge: 0, name: 'Icelandic Króna'}, // See above.
  {code: 'JMD', digits: 2, minCharge: 0, name: 'Jamaican Dollar'},
  {code: 'JPY', digits: 0, minCharge: 50, name: 'Japanese Yen'},
  {code: 'KES', digits: 2, minCharge: 0, name: 'Kenyan Shilling'},
  {code: 'KGS', digits: 2, minCharge: 0, name: 'Kyrgyzstan Som'},
  {code: 'KHR', digits: 2, minCharge: 0, name: 'Cambodian Riel'},
  {code: 'KMF', digits: 0, minCharge: 0, name: 'Comorian Franc'},
  {code: 'KRW', digits: 0, minCharge: 0, name: 'South Korean Won'},
  {code: 'KYD', digits: 2, minCharge: 0, name: 'Cayman Islands Dollar'},
  {code: 'KZT', digits: 2, minCharge: 0, name: 'Kazakhstani Tenge'},
  {code: 'LAK', digits: 2, minCharge: 0, name: 'Lao Kip'},
  {code: 'LBP', digits: 2, minCharge: 0, name: 'Lebanese Pound'},
  {code: 'LKR', digits: 2, minCharge: 0, name: 'Sri Lankan Rupee'},
  {code: 'LRD', digits: 2, minCharge: 0, name: 'Liberian Dollar'},
  {code: 'LSL', digits: 2, minCharge: 0, name: 'Lesotho Loti'},
  {code: 'MAD', digits: 2, minCharge: 0, name: 'Moroccan Dirham'},
  {code: 'MDL', digits: 2, minCharge: 0, name: 'Moldovan Leu'},
  {code: 'MGA', digits: 0, minCharge: 0, name: 'Madagascar Ariary'}, // See above.
  {code: 'MKD', digits: 2, minCharge: 0, name: 'Macedonia Denar'},
  {code: 'MMK', digits: 2, minCharge: 0, name: 'Myanmar (Burma) Kyat'},
  {code: 'MNT', digits: 2, minCharge: 0, name: 'Mongolian Tögrög'},
  {code: 'MOP', digits: 2, minCharge: 0, name: 'Macanese Pataca'},
  {code: 'MRO', digits: 2, minCharge: 0, name: 'Mauritanian Ouguiya'},
  {code: 'MUR', digits: 2, minCharge: 0, name: 'Mauritian Rupee'},
  {code: 'MVR', digits: 2, minCharge: 0, name: 'Maldivian Rufiyaa'},
  {code: 'MWK', digits: 2, minCharge: 0, name: 'Malawian Kwacha'},
  {code: 'MXN', digits: 2, minCharge: 1000, name: 'Mexican Peso'},
  {code: 'MYR', digits: 2, minCharge: 200, name: 'Malaysian Ringgit'},
  {code: 'MZN', digits: 2, minCharge: 0, name: 'Mozambique Metical'},
  {code: 'NAD', digits: 2, minCharge: 0, name: 'Namibian Dollar'},
  {code: 'NGN', digits: 2, minCharge: 0, name: 'Nigerian Naira'},
  {code: 'NIO', digits: 2, minCharge: 0, name: 'Nicaraguan Córdoba'},
  {code: 'NOK', digits: 2, minCharge: 300, name: 'Norwegian Krone'},
  {code: 'NPR', digits: 2, minCharge: 0, name: 'Nepalese Rupee'},
  {code: 'NZD', digits: 2, minCharge: 50, name: 'New Zealand Dollar'},
  {code: 'PAB', digits: 2, minCharge: 0, name: 'Panamanian Balboa'},
  {code: 'PEN', digits: 2, minCharge: 0, name: 'Peruvian Nuevo Sol'},
  {code: 'PGK', digits: 2, minCharge: 0, name: 'Papua New Guinean Kina'},
  {code: 'PHP', digits: 2, minCharge: 0, name: 'Philippine Peso'},
  {code: 'PKR', digits: 2, minCharge: 0, name: 'Pakistani Rupee'},
  {code: 'PLN', digits: 2, minCharge: 200, name: 'Polish Złoty'},
  {code: 'PYG', digits: 0, minCharge: 0, name: 'Paraguayan Guaraní'},
  {code: 'QAR', digits: 2, minCharge: 0, name: 'Qatari Riyal'},
  {code: 'RON', digits: 2, minCharge: 0, name: 'Romania Leu'},
  {code: 'RSD', digits: 2, minCharge: 0, name: 'Serbia Dinar'},
  {code: 'RUB', digits: 2, minCharge: 0, name: 'Russian Ruble'},
  {code: 'RWF', digits: 0, minCharge: 0, name: 'Rwanda Franc'},
  {code: 'SAR', digits: 2, minCharge: 0, name: 'Saudi Riyal'},
  {code: 'SBD', digits: 2, minCharge: 0, name: 'Solomon Islands Dollar'},
  {code: 'SCR', digits: 2, minCharge: 0, name: 'Seychellois Rupee'},
  {code: 'SEK', digits: 2, minCharge: 300, name: 'Swedish Krona'},
  {code: 'SGD', digits: 2, minCharge: 50, name: 'Singapore Dollar'},
  {code: 'SHP', digits: 2, minCharge: 0, name: 'Saint Helenian Pound'},
  {code: 'SLL', digits: 2, minCharge: 0, name: 'Sierra Leonean Leone'},
  {code: 'SOS', digits: 2, minCharge: 0, name: 'Somali Shilling'},
  {code: 'SRD', digits: 2, minCharge: 0, name: 'Suriname Dollar'},
  {code: 'STD', digits: 2, minCharge: 0, name: 'São Tomé and Príncipe Dobra'},
  {code: 'SZL', digits: 2, minCharge: 0, name: 'Swazi Lilangeni'},
  {code: 'THB', digits: 2, minCharge: 0, name: 'Thai Baht'},
  {code: 'TJS', digits: 2, minCharge: 0, name: 'Tajikistan Somoni'},
  {code: 'TOP', digits: 2, minCharge: 0, name: 'Tongan Paʻanga'},
  {code: 'TRY', digits: 2, minCharge: 0, name: 'Turkey Lira'},
  {code: 'TTD', digits: 2, minCharge: 0, name: 'Trinidad and Tobago Dollar'},
  {code: 'TWD', digits: 2, minCharge: 0, name: 'New Taiwan Dollar'},
  {code: 'TZS', digits: 2, minCharge: 0, name: 'Tanzanian Shilling'},
  {code: 'UAH', digits: 2, minCharge: 0, name: 'Ukrainian Hryvnia'},
  {code: 'UGX', digits: 0, minCharge: 0, name: 'Ugandan Shilling'}, // See above.
  {code: 'USD', digits: 2, minCharge: 50, name: 'United States Dollar'},
  {code: 'UYU', digits: 2, minCharge: 0, name: 'Uruguayan Peso'},
  {code: 'UZS', digits: 2, minCharge: 0, name: 'Uzbekistani Som'},
  {code: 'VND', digits: 0, minCharge: 0, name: 'Vietnamese Đồng'},
  {code: 'VUV', digits: 0, minCharge: 0, name: 'Vanuatu Vatu'},
  {code: 'WST', digits: 2, minCharge: 0, name: 'Samoan Tala'},
  {code: 'XAF', digits: 0, minCharge: 0, name: 'Central African Cfa Franc'},
  {code: 'XCD', digits: 2, minCharge: 0, name: 'East Caribbean Dollar'},
  {code: 'XOF', digits: 0, minCharge: 0, name: 'West African Cfa Franc'},
  {code: 'XPF', digits: 0, minCharge: 0, name: 'Cfp Franc'},
  {code: 'YER', digits: 2, minCharge: 0, name: 'Yemeni Rial'},
  {code: 'ZAR', digits: 2, minCharge: 0, name: 'South African Rand'},
  {code: 'ZMW', digits: 2, minCharge: 0, name: 'Zambia Kwacha'},
];

// used to determine Stripe support for a currency
export const stripeCurrencyCodes: {
  [key: string]: boolean;
} = stripeCurrencyList.reduce<Record<string, any>>(function (acc, currency) {
  acc[currency.code] = true;
  return acc;
}, {});

// Note: These currencies are extracted by getting PayPal compatible currencies from:
//
// curl https://developer.paypal.com/docs/api/reference/currency-codes
//
// And then populating in the names from ISO 4217:

export const paypalCurrencyList: Array<Currency> = [
  /*
   * ----------------------------------------------------------------------------
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   *
   * This const is duplicated to @packages/systems/commerce/constants/paypalCurrencyList.js
   * to avoid bundling this entire SharedConfig file in the webflow.js published
   * site bundle. Once we have support for bundling for the Dashboard (this
   * config is injected in to window.SharedConfig for angular), we should
   * be able to delete this const and import directly from the package above.
   *
   * In the meantime, if you make changes to this const, please make sure to
   * update in the other location as well.
   *
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE NOTE
   * ----------------------------------------------------------------------------
   */

  {code: 'AUD', digits: 2, minCharge: 1, name: 'Australian Dollar'},
  {code: 'BRL', digits: 2, minCharge: 1, name: 'Brazilian Real'},
  {code: 'CAD', digits: 2, minCharge: 1, name: 'Canadian Dollar'},
  {code: 'CNY', digits: 2, minCharge: 1, name: 'Chinese Renmenbi'},
  {code: 'CZK', digits: 2, minCharge: 1, name: 'Czech Koruna'},
  {code: 'DKK', digits: 2, minCharge: 1, name: 'Danish Krone'},
  {code: 'EUR', digits: 2, minCharge: 1, name: 'Euro'},
  {code: 'HKD', digits: 2, minCharge: 1, name: 'Hong Kong Dollar'},
  // HUF is currently disabled, due to issues with PayPal's 0-decimal implementation
  // See: https://github.com/webflow/webflow/issues/32865
  // {code: 'HUF', digits: 0, minCharge: 1, name: 'Hungarian Forint'},
  {code: 'INR', digits: 2, minCharge: 1, name: 'Indian Rupee'},
  {code: 'ILS', digits: 2, minCharge: 1, name: 'Israeli New Sheqel'},
  {code: 'JPY', digits: 0, minCharge: 1, name: 'Japanese Yen'},
  {code: 'MYR', digits: 2, minCharge: 1, name: 'Malaysian Ringgit'},
  {code: 'MXN', digits: 2, minCharge: 1, name: 'Mexican Peso'},
  {code: 'TWD', digits: 0, minCharge: 1, name: 'New Taiwan Dollar'},
  {code: 'NZD', digits: 2, minCharge: 1, name: 'New Zealand Dollar'},
  {code: 'NOK', digits: 2, minCharge: 1, name: 'Norwegian Krone'},
  {code: 'PHP', digits: 2, minCharge: 1, name: 'Philippine Peso'},
  {code: 'PLN', digits: 2, minCharge: 1, name: 'Polish Złoty'},
  {code: 'GBP', digits: 2, minCharge: 1, name: 'British Pound'},
  {code: 'RUB', digits: 2, minCharge: 1, name: 'Russian Ruble'},
  {code: 'SGD', digits: 2, minCharge: 1, name: 'Singapore Dollar'},
  {code: 'SEK', digits: 2, minCharge: 1, name: 'Swedish Krona'},
  {code: 'CHF', digits: 2, minCharge: 1, name: 'Swiss Franc'},
  {code: 'THB', digits: 2, minCharge: 1, name: 'Thai Baht'},
  {code: 'USD', digits: 2, minCharge: 1, name: 'United States Dollar'},
];

// used to determine PayPal support for a currency
export const paypalCurrencyCodes: {
  [key: string]: boolean;
} = paypalCurrencyList.reduce<Record<string, any>>(function (acc, currency) {
  acc[currency.code] = true;
  return acc;
}, {});

type CommerceCurrencyLocale = {
  name: string;
  location: string;
  tag: string;
};

// list of all the locales for the list of countries provided in commerceCountryList
export const commerceCurrencyLocales: Array<CommerceCurrencyLocale> = [
  {name: 'Afar', location: 'Djibouti', tag: 'aa-DJ'},
  {name: 'Afar', location: 'Eritrea', tag: 'aa-ER'},
  {name: 'Afar', location: 'Ethiopia', tag: 'aa-ET'},
  {name: 'Afrikaans', location: 'Namibia', tag: 'af-NA'},
  {name: 'Afrikaans', location: 'South Africa', tag: 'af-ZA'},
  {name: 'Aghem', location: 'Cameroon', tag: 'agq-CM'},
  {name: 'Akan', location: 'Ghana', tag: 'ak-GH'},
  {name: 'Albanian', location: 'Albania', tag: 'sq-AL'},
  {name: 'Alsatian', location: 'France', tag: 'gsw-FR'},
  {name: 'Alsatian', location: 'Liechtenstein', tag: 'gsw-LI'},
  {name: 'Alsatian', location: 'Switzerland', tag: 'gsw-CH'},
  {name: 'Amharic', location: 'Ethiopia', tag: 'am-ET'},
  {name: 'Arabic', location: 'Algeria', tag: 'ar-DZ'},
  {name: 'Arabic', location: 'Bahrain', tag: 'ar-BH'},
  {name: 'Arabic', location: 'Chad', tag: 'ar-TD'},
  {name: 'Arabic', location: 'Comoros', tag: 'ar-KM'},
  {name: 'Arabic', location: 'Djibouti', tag: 'ar-DJ'},
  {name: 'Arabic', location: 'Egypt', tag: 'ar-EG'},
  {name: 'Arabic', location: 'Eritrea', tag: 'ar-ER'},
  {name: 'Arabic', location: 'Iraq', tag: 'ar-IQ'},
  {name: 'Arabic', location: 'Israel', tag: 'ar-IL'},
  {name: 'Arabic', location: 'Jordan', tag: 'ar-JO'},
  {name: 'Arabic', location: 'Kuwait', tag: 'ar-KW'},
  {name: 'Arabic', location: 'Lebanon', tag: 'ar-LB'},
  {name: 'Arabic', location: 'Libya', tag: 'ar-LY'},
  {name: 'Arabic', location: 'Mauritania', tag: 'ar-MR'},
  {name: 'Arabic', location: 'Morocco', tag: 'ar-MA'},
  {name: 'Arabic', location: 'Oman', tag: 'ar-OM'},
  {name: 'Arabic', location: 'Qatar', tag: 'ar-QA'},
  {name: 'Arabic', location: 'Saudi Arabia', tag: 'ar-SA'},
  {name: 'Arabic', location: 'Somalia', tag: 'ar-SO'},
  {name: 'Arabic', location: 'South Sudan', tag: 'ar-SS'},
  {name: 'Arabic', location: 'Sudan', tag: 'ar-SD'},
  {name: 'Arabic', location: 'Syria', tag: 'ar-SY'},
  {name: 'Arabic', location: 'Tunisia', tag: 'ar-TN'},
  {name: 'Arabic', location: 'Yemen', tag: 'ar-YE'},
  {name: 'Armenian', location: 'Armenia', tag: 'hy-AM'},
  {name: 'Assamese', location: 'India', tag: 'as-IN'},
  {name: 'Asturian', location: 'Spain', tag: 'ast-ES'},
  {name: 'Asu', location: 'Tanzania', tag: 'asa-TZ'},
  {name: 'Azerbaijani (Cyrillic)', location: 'Azerbaijan', tag: 'az-Cyrl-AZ'},
  {name: 'Azerbaijani (Latin)', location: 'Azerbaijan', tag: 'az-Latn-AZ'},
  {name: 'Bafia', location: 'Cameroon', tag: 'ksf-CM'},
  {name: 'Bamanankan (Latin)', location: 'Mali', tag: 'bm-Latn-ML'},
  {name: 'Bangla', location: 'Bangladesh', tag: 'bn-BD'},
  {name: 'Bangla', location: 'India', tag: 'bn-IN'},
  {name: 'Basaa', location: 'Cameroon', tag: 'bas-CM'},
  {name: 'Bashkir', location: 'Russia', tag: 'ba-RU'},
  {name: 'Basque', location: 'Spain', tag: 'eu-ES'},
  {name: 'Belarusian', location: 'Belarus', tag: 'be-BY'},
  {name: 'Bemba', location: 'Zambia', tag: 'bem-ZM'},
  {name: 'Bena', location: 'Tanzania', tag: 'bez-TZ'},
  {name: 'Blin', location: 'Eritrea', tag: 'byn-ER'},
  {name: 'Bodo', location: 'India', tag: 'brx-IN'},
  {
    name: 'Bosnian (Cyrillic)',
    location: 'Bosnia and Herzegovina',
    tag: 'bs-Cyrl-BA',
  },
  {
    name: 'Bosnian (Latin)',
    location: 'Bosnia and Herzegovina',
    tag: 'bs-Latn-BA',
  },
  {name: 'Breton', location: 'France', tag: 'br-FR'},
  {name: 'Bulgarian', location: 'Bulgaria', tag: 'bg-BG'},
  {name: 'Burmese', location: 'Myanmar', tag: 'my-MM'},
  {name: 'Catalan', location: 'Andorra', tag: 'ca-AD'},
  {name: 'Catalan', location: 'France', tag: 'ca-FR'},
  {name: 'Catalan', location: 'Italy', tag: 'ca-IT'},
  {name: 'Catalan', location: 'Spain', tag: 'ca-ES'},
  {name: 'Central Atlas Tamazight ', location: 'Morocco', tag: 'tzm-Latn'},
  {name: 'Central Kurdish', location: 'Iraq', tag: 'ku-Arab-IQ'},
  {name: 'Chechen', location: 'Russia', tag: 'cd-RU'},
  {name: 'Cherokee', location: 'United States', tag: 'chr-Cher-US'},
  {name: 'Chiga', location: 'Uganda', tag: 'cgg-UG'},
  {name: 'Chinese (Simplified)', location: 'Singapore', tag: 'zh-SG'},
  {name: 'Chinese (Traditional)', location: 'Taiwan', tag: 'zh-TW'},
  {name: 'Church Slavic', location: 'Russia', tag: 'cu-RU'},
  {name: 'Cornish', location: 'United Kingdom', tag: 'kw-GB'},
  {name: 'Corsican', location: 'France', tag: 'co-FR'},
  {name: 'Croatian', location: 'Croatia', tag: 'hr-HR'},
  {
    name: 'Croatian (Latin)',
    location: 'Bosnia and Herzegovina',
    tag: 'hr-BA',
  },
  {name: 'Czech', location: 'Czechia', tag: 'cs-CZ'},
  {name: 'Danish', location: 'Denmark', tag: 'da-DK'},
  {name: 'Danish', location: 'Greenland', tag: 'da-GL'},
  {name: 'Dari', location: 'Afghanistan', tag: 'prs-AF'},
  {name: 'Divehi', location: 'Maldives', tag: 'dv-MV'},
  {name: 'Duala', location: 'Cameroon', tag: 'dua-CM'},
  {name: 'Dutch', location: 'Aruba', tag: 'nl-AW'},
  {name: 'Dutch', location: 'Belgium', tag: 'nl-BE'},
  {name: 'Dutch', location: 'Netherlands', tag: 'nl-NL'},
  {name: 'Dutch', location: 'Sint Maarten', tag: 'nl-SX'},
  {name: 'Dutch', location: 'Suriname', tag: 'nl-SR'},
  {name: 'Dzongkha', location: 'Bhutan', tag: 'dz-BT'},
  {name: 'Embu', location: 'Kenya', tag: 'ebu-KE'},
  {name: 'English', location: 'American Samoa', tag: 'en-AS'},
  {name: 'English', location: 'Anguilla', tag: 'en-AI'},
  {name: 'English', location: 'Antigua and Barbuda', tag: 'en-AG'},
  {name: 'English', location: 'Australia', tag: 'en-AU'},
  {name: 'English', location: 'Austria', tag: 'en-AT'},
  {name: 'English', location: 'Bahamas', tag: 'en-BS'},
  {name: 'English', location: 'Barbados', tag: 'en-BB'},
  {name: 'English', location: 'Belgium', tag: 'en-BE'},
  {name: 'English', location: 'Belize', tag: 'en-BZ'},
  {name: 'English', location: 'Bermuda', tag: 'en-BM'},
  {name: 'English', location: 'Botswana', tag: 'en-BW'},
  {name: 'English', location: 'British Indian Ocean Territory', tag: 'en-IO'},
  {name: 'English', location: 'British Virgin Islands', tag: 'en-VG'},
  {name: 'English', location: 'Burundi', tag: 'en-BI'},
  {name: 'English', location: 'Cameroon', tag: 'en-CM'},
  {name: 'English', location: 'Canada', tag: 'en-CA'},
  {name: 'English', location: 'Cayman Islands', tag: 'en-KY'},
  {name: 'English', location: 'Christmas Island', tag: 'en-CX'},
  {name: 'English', location: 'Cook Islands', tag: 'en-CK'},
  {name: 'English', location: 'Cyprus', tag: 'en-CY'},
  {name: 'English', location: 'Denmark', tag: 'en-DK'},
  {name: 'English', location: 'Dominica', tag: 'en-DM'},
  {name: 'English', location: 'Eritrea', tag: 'en-ER'},
  {name: 'English', location: 'Falkland Islands', tag: 'en-FK'},
  {name: 'English', location: 'Finland', tag: 'en-FI'},
  {name: 'English', location: 'Fiji', tag: 'en-FJ'},
  {name: 'English', location: 'Gambia', tag: 'en-GM'},
  {name: 'English', location: 'Germany', tag: 'en-DE'},
  {name: 'English', location: 'Ghana', tag: 'en-GH'},
  {name: 'English', location: 'Gibraltar', tag: 'en-GI'},
  {name: 'English', location: 'Grenada', tag: 'en-GD'},
  {name: 'English', location: 'Guam', tag: 'en-GU'},
  {name: 'English', location: 'Guernsey', tag: 'en-GG'},
  {name: 'English', location: 'Guyana', tag: 'en-GY'},
  {name: 'English', location: 'Hong Kong', tag: 'en-HK'},
  {name: 'English', location: 'India', tag: 'en-IN'},
  {name: 'English', location: 'Ireland', tag: 'en-IE'},
  {name: 'English', location: 'Isle of Man', tag: 'en-IM'},
  {name: 'English', location: 'Israel', tag: 'en-IL'},
  {name: 'English', location: 'Jamaica', tag: 'en-JM'},
  {name: 'English', location: 'Jersey', tag: 'en-JE'},
  {name: 'English', location: 'Kenya', tag: 'en-KE'},
  {name: 'English', location: 'Kiribati', tag: 'en-KI'},
  {name: 'English', location: 'Lesotho', tag: 'en-LS'},
  {name: 'English', location: 'Liberia', tag: 'en-LR'},
  {name: 'English', location: 'Madagascar', tag: 'en-MG'},
  {name: 'English', location: 'Malawi', tag: 'en-MW'},
  {name: 'English', location: 'Malaysia', tag: 'en-MY'},
  {name: 'English', location: 'Malta', tag: 'en-MT'},
  {name: 'English', location: 'Marshall Islands', tag: 'en-MH'},
  {name: 'English', location: 'Mauritius', tag: 'en-MU'},
  {name: 'English', location: 'Micronesia', tag: 'en-FM'},
  {name: 'English', location: 'Montserrat', tag: 'en-MS'},
  {name: 'English', location: 'Namibia', tag: 'en-NA'},
  {name: 'English', location: 'Nauru', tag: 'en-NR'},
  {name: 'English', location: 'Netherlands', tag: 'en-NL'},
  {name: 'English', location: 'New Zealand', tag: 'en-NZ'},
  {name: 'English', location: 'Nigeria', tag: 'en-NG'},
  {name: 'English', location: 'Niue', tag: 'en-NU'},
  {name: 'English', location: 'Norfolk Island', tag: 'en-NF'},
  {name: 'English', location: 'Northern Mariana Islands', tag: 'en-MP'},
  {name: 'English', location: 'Pakistan', tag: 'en-PK'},
  {name: 'English', location: 'Palau', tag: 'en-PW'},
  {name: 'English', location: 'Papua New Guinea', tag: 'en-PG'},
  {name: 'English', location: 'Puerto Rico', tag: 'en-PR'},
  {name: 'English', location: 'Rwanda', tag: 'en-RW'},
  {name: 'English', location: 'Saint Kitts and Nevis', tag: 'en-KN'},
  {name: 'English', location: 'Saint Lucia', tag: 'en-LC'},
  {
    name: 'English',
    location: 'Saint Vincent and the Grenadines',
    tag: 'en-VC',
  },
  {name: 'English', location: 'Samoa', tag: 'en-WS'},
  {name: 'English', location: 'Seychelles', tag: 'en-SC'},
  {name: 'English', location: 'Sierra Leone', tag: 'en-SL'},
  {name: 'English', location: 'Singapore', tag: 'en-SG'},
  {name: 'English', location: 'Sint Maarten', tag: 'en-SX'},
  {name: 'English', location: 'Slovenia', tag: 'en-SI'},
  {name: 'English', location: 'Solomon Islands', tag: 'en-SB'},
  {name: 'English', location: 'South Africa', tag: 'en-ZA'},
  {name: 'English', location: 'South Sudan', tag: 'en-SS'},
  {name: 'English', location: 'Sudan', tag: 'en-SD'},
  {name: 'English', location: 'Swaziland', tag: 'en-SZ'},
  {name: 'English', location: 'Sweden', tag: 'en-SE'},
  {name: 'English', location: 'Switzerland', tag: 'en-CH'},
  {name: 'English', location: 'Tanzania', tag: 'en-TZ'},
  {name: 'English', location: 'Tokelau', tag: 'en-TK'},
  {name: 'English', location: 'Tonga', tag: 'en-TO'},
  {name: 'English', location: 'Trinidad and Tobago', tag: 'en-TT'},
  {name: 'English', location: 'Turks and Caicos Islands', tag: 'en-TC'},
  {name: 'English', location: 'Tuvalu', tag: 'en-TV'},
  {name: 'English', location: 'Uganda', tag: 'en-UG'},
  {name: 'English', location: 'United Kingdom', tag: 'en-GB'},
  {name: 'English', location: 'United States', tag: 'en-US'},
  {name: 'English', location: 'Vanuatu', tag: 'en-VU'},
  {name: 'English', location: 'Zambia', tag: 'en-ZM'},
  {name: 'English', location: 'Zimbabwe', tag: 'en-ZW'},
  {name: 'Estonian', location: 'Estonia', tag: 'et-EE'},
  {name: 'Ewe', location: 'Ghana', tag: 'ee-GH'},
  {name: 'Ewe', location: 'Togo', tag: 'ee-TG'},
  {name: 'Ewondo', location: 'Cameroon', tag: 'ewo-CM'},
  {name: 'Faroese', location: 'Denmark', tag: 'fo-DK'},
  {name: 'Faroese', location: 'Faroe Islands', tag: 'fo-FO'},
  {name: 'Filipino', location: 'Philippines', tag: 'fil-PH'},
  {name: 'Finnish', location: 'Finland', tag: 'fi-FI'},
  {name: 'French', location: 'Algeria', tag: 'fr-DZ'},
  {name: 'French', location: 'Belgium', tag: 'fr-BE'},
  {name: 'French', location: 'Benin', tag: 'fr-BJ'},
  {name: 'French', location: 'Burkina Faso', tag: 'fr-BF'},
  {name: 'French', location: 'Burundi', tag: 'fr-BI'},
  {name: 'French', location: 'Cameroon', tag: 'fr-CM'},
  {name: 'French', location: 'Canada', tag: 'fr-CA'},
  {name: 'French', location: 'Central African Republic', tag: 'fr-CF'},
  {name: 'French', location: 'Chad', tag: 'fr-TD'},
  {name: 'French', location: 'Comoros', tag: 'fr-KM'},
  {name: 'French', location: 'Djibouti', tag: 'fr-DJ'},
  {name: 'French', location: 'Equatorial Guinea', tag: 'fr-GQ'},
  {name: 'French', location: 'France', tag: 'fr-FR'},
  {name: 'French', location: 'French Guiana', tag: 'fr-GF'},
  {name: 'French', location: 'French Polynesia', tag: 'fr-PF'},
  {name: 'French', location: 'Gabon', tag: 'fr-GA'},
  {name: 'French', location: 'Guadeloupe', tag: 'fr-GP'},
  {name: 'French', location: 'Guinea', tag: 'fr-GN'},
  {name: 'French', location: 'Haiti', tag: 'fr-HT'},
  {name: 'French', location: 'Luxembourg', tag: 'fr-LU'},
  {name: 'French', location: 'Madagascar', tag: 'fr-MG'},
  {name: 'French', location: 'Mali', tag: 'fr-ML'},
  {name: 'French', location: 'Martinique', tag: 'fr-MQ'},
  {name: 'French', location: 'Mauritania', tag: 'fr-MR'},
  {name: 'French', location: 'Mauritius', tag: 'fr-MU'},
  {name: 'French', location: 'Mayotte', tag: 'fr-YT'},
  {name: 'French', location: 'Morocco', tag: 'fr-MA'},
  {name: 'French', location: 'New Caledonia', tag: 'fr-NC'},
  {name: 'French', location: 'Niger', tag: 'fr-NE'},
  {name: 'French', location: 'Reunion', tag: 'fr-RE'},
  {name: 'French', location: 'Rwanda', tag: 'fr-RW'},
  {name: 'French', location: 'Saint Martin', tag: 'fr-MF'},
  {name: 'French', location: 'Saint Pierre and Miquelon', tag: 'fr-PM'},
  {name: 'French', location: 'Senegal', tag: 'fr-SN'},
  {name: 'French', location: 'Seychelles', tag: 'fr-SC'},
  {name: 'French', location: 'Switzerland', tag: 'fr-CH'},
  {name: 'French', location: 'Syria', tag: 'fr-SY'},
  {name: 'French', location: 'Togo', tag: 'fr-TG'},
  {name: 'French', location: 'Tunisia', tag: 'fr-TN'},
  {name: 'French', location: 'Vanuatu', tag: 'fr-VU'},
  {name: 'French', location: 'Wallis and Futuna', tag: 'fr-WF'},
  {name: 'Frisian', location: 'Netherlands', tag: 'fy-NL'},
  {name: 'Friulian', location: 'Italy', tag: 'fur-IT'},
  {name: 'Fulah (Latin)', location: 'Burkina Faso', tag: 'ff-Latn-BF'},
  {name: 'Fulah (Latin)', location: 'Cameroon', tag: 'ff-Latn-CM'},
  {name: 'Fulah (Latin)', location: 'Gambia', tag: 'ff-Latn-GM'},
  {name: 'Fulah (Latin)', location: 'Ghana', tag: 'ff-Latn-GH'},
  {name: 'Fulah (Latin)', location: 'Guinea', tag: 'ff-Latn-GN'},
  {name: 'Fulah (Latin)', location: 'Guinea-Bissau', tag: 'ff-Latn-GW'},
  {name: 'Fulah (Latin)', location: 'Liberia', tag: 'ff-Latn-LR'},
  {name: 'Fulah (Latin)', location: 'Mauritania', tag: 'ff-Latn-MR'},
  {name: 'Fulah (Latin)', location: 'Niger', tag: 'ff-Latn-NE'},
  {name: 'Fulah (Latin)', location: 'Nigeria', tag: 'ff-Latn-NG'},
  {name: 'Fulah (Latin)', location: 'Sierra Leone', tag: 'ff-Latn-SL'},
  {name: 'Fulah', location: 'Cameroon', tag: 'ff-CM'},
  {name: 'Fulah', location: 'Guinea', tag: 'ff-GN'},
  {name: 'Fulah', location: 'Mauritania', tag: 'ff-MR'},
  {name: 'Fulah', location: 'Nigeria', tag: 'ff-NG'},
  {name: 'Fulah', location: 'Senegal', tag: 'ff-Latn-SN'},
  {name: 'Galician', location: 'Spain', tag: 'gl-ES'},
  {name: 'Ganda', location: 'Uganda', tag: 'lg-UG'},
  {name: 'Georgian', location: 'Georgia', tag: 'ka-GE'},
  {name: 'German', location: 'Austria', tag: 'de-AT'},
  {name: 'German', location: 'Belgium', tag: 'de-BE'},
  {name: 'German', location: 'Germany', tag: 'de-DE'},
  {name: 'German', location: 'Italy', tag: 'de-IT'},
  {name: 'German', location: 'Liechtenstein', tag: 'de-LI'},
  {name: 'German', location: 'Luxembourg', tag: 'de-LU'},
  {name: 'German', location: 'Switzerland', tag: 'de-CH'},
  {name: 'Greek', location: 'Cyprus', tag: 'el-CY'},
  {name: 'Greek', location: 'Greece', tag: 'el-GR'},
  {name: 'Greenlandic', location: 'Greenland', tag: 'kl-GL'},
  {name: 'Guarani', location: 'Paraguay', tag: 'gn-PY'},
  {name: 'Gujarati', location: 'India', tag: 'gu-IN'},
  {name: 'Gusii', location: 'Kenya', tag: 'guz-KE'},
  {name: 'Hausa (Latin)', location: 'Ghana', tag: 'ha-Latn-GH'},
  {name: 'Hausa (Latin)', location: 'Niger', tag: 'ha-Latn-NE'},
  {name: 'Hausa (Latin)', location: 'Nigeria', tag: 'ha-Latn-NG'},
  {name: 'Hawaiian', location: 'United States', tag: 'haw-US'},
  {name: 'Hebrew', location: 'Israel', tag: 'he-IL'},
  {name: 'Hindi', location: 'India', tag: 'hi-IN'},
  {name: 'Hungarian', location: 'Hungary', tag: 'hu-HU'},
  {name: 'Icelandic', location: 'Iceland', tag: 'is-IS'},
  {name: 'Igbo', location: 'Nigeria', tag: 'ig-NG'},
  {name: 'Indonesian', location: 'Indonesia', tag: 'id-ID'},
  {name: 'Interlingua', location: 'France', tag: 'ia-FR'},
  {name: 'Inuktitut (Latin)', location: 'Canada', tag: 'iu-Latn-CA'},
  {name: 'Inuktitut (Syllabics)', location: 'Canada', tag: 'iu-Cans-CA'},
  {name: 'Irish', location: 'Ireland', tag: 'ga-IE'},
  {name: 'Italian', location: 'Italy', tag: 'it-IT'},
  {name: 'Italian', location: 'San Marino', tag: 'it-SM'},
  {name: 'Italian', location: 'Switzerland', tag: 'it-CH'},
  {name: 'Japanese', location: 'Japan', tag: 'ja-JP'},
  {name: 'Jola-Fonyi', location: 'Senegal', tag: 'dyo-SN'},
  {name: 'Kabyle', location: 'Algeria', tag: 'kab-DZ'},
  {name: 'Kako', location: 'Cameroon', tag: 'kkj-CM'},
  {name: 'Kalenjin', location: 'Kenya', tag: 'kln-KE'},
  {name: 'Kamba', location: 'Kenya', tag: 'kam-KE'},
  {name: 'Kannada', location: 'India', tag: 'kn-IN'},
  {name: 'Kazakh', location: 'Kazakhstan', tag: 'kk-KZ'},
  {name: 'Khmer', location: 'Cambodia', tag: 'km-KH'},
  {name: "K'iche", location: 'Guatemala', tag: 'quc-Latn-GT'},
  {name: 'Kikuyu', location: 'Kenya', tag: 'ki-KE'},
  {name: 'Kinyarwanda', location: 'Rwanda', tag: 'rw-RW'},
  {name: 'Kiswahili', location: 'Kenya', tag: 'sw-KE'},
  {name: 'Kiswahili', location: 'Tanzania', tag: 'sw-TZ'},
  {name: 'Kiswahili', location: 'Uganda', tag: 'sw-UG'},
  {name: 'Konkani', location: 'India', tag: 'kok-IN'},
  {name: 'Korean', location: 'North Korea', tag: 'ko-KP'},
  {name: 'Koyra Chiini', location: 'Mali', tag: 'khq-ML'},
  {name: 'Koyraboro Senni', location: 'Mali', tag: 'ses-ML'},
  {name: 'Kwasio', location: 'Cameroon', tag: 'nmg-CM'},
  {name: 'Kyrgyz', location: 'Kyrgyzstan', tag: 'ky-KG'},
  {name: 'Lakota', location: 'United States', tag: 'lkt-US'},
  {name: 'Langi', location: 'Tanzania', tag: 'lag-TZ'},
  {name: 'Latvian', location: 'Latvia', tag: 'lv-LV'},
  {name: 'Lingala', location: 'Angola', tag: 'ln-AO'},
  {name: 'Lingala', location: 'Central African Republic', tag: 'ln-CF'},
  {name: 'Lithuanian', location: 'Lithuania', tag: 'lt-LT'},
  {name: 'Low German ', location: 'Germany', tag: 'nds-DE'},
  {name: 'Low German', location: 'Netherlands', tag: 'nds-NL'},
  {name: 'Lower Sorbian', location: 'Germany', tag: 'dsb-DE'},
  {name: 'Luo', location: 'Kenya', tag: 'luo-KE'},
  {name: 'Luxembourgish', location: 'Luxembourg', tag: 'lb-LU'},
  {name: 'Luyia', location: 'Kenya', tag: 'luy-KE'},
  {name: 'Machame', location: 'Tanzania', tag: 'jmc-TZ'},
  {name: 'Makhuwa-Meetto', location: 'Mozambique', tag: 'mgh-MZ'},
  {name: 'Makonde', location: 'Tanzania', tag: 'kde-TZ'},
  {name: 'Malagasy', location: 'Madagascar', tag: 'mg-MG'},
  {name: 'Malay', location: 'Malaysia', tag: 'ms-MY'},
  {name: 'Malayalam', location: 'India', tag: 'ml-IN'},
  {name: 'Maltese', location: 'Malta', tag: 'mt-MT'},
  {name: 'Manx', location: 'Isle of Man', tag: 'gv-IM'},
  {name: 'Maori', location: 'New Zealand', tag: 'mi-NZ'},
  {name: 'Mapudungun', location: 'Chile', tag: 'arn-CL'},
  {name: 'Marathi', location: 'India', tag: 'mr-IN'},
  {name: 'Masai', location: 'Kenya', tag: 'mas-KE'},
  {name: 'Masai', location: 'Tanzania', tag: 'mas-TZ'},
  {name: 'Mazanderani', location: 'Iran', tag: 'mzn-IR'},
  {name: 'Meru', location: 'Kenya', tag: 'mer-KE'},
  {name: "Meta'", location: 'Cameroon', tag: 'mgo-CM'},
  {name: 'Mohawk', location: 'Canada', tag: 'moh-CA'},
  {name: 'Mongolian (Cyrillic)', location: 'Mongolia', tag: 'mn-MN'},
  {name: 'Mongolian (Traditional)', location: 'Mongolia', tag: 'mn-Mong'},
  {name: 'Morisyen', location: 'Mauritius', tag: 'mfe-MU'},
  {name: 'Mundang', location: 'Cameroon', tag: 'mua-CM'},
  {name: "N'ko", location: 'Guinea', tag: 'nqo-GN'},
  {name: 'Nama', location: 'Namibia', tag: 'naq-NA'},
  {name: 'Nepali', location: 'India', tag: 'ne-IN'},
  {name: 'Nepali', location: 'Nepal', tag: 'ne-NP'},
  {name: 'Ngiemboon', location: 'Cameroon', tag: 'nnh-CM'},
  {name: 'Ngomba', location: 'Cameroon', tag: 'jgo-CM'},
  {name: 'Northern Luri', location: 'Iraq', tag: 'lrc-IQ'},
  {name: 'Northern Luri', location: 'Iran', tag: 'lrc-IR'},
  {name: 'North Ndebele', location: 'Zimbabwe', tag: 'nd-ZW'},
  {name: 'Norwegian (Bokmal)', location: 'Norway', tag: 'nb-NO'},
  {name: 'Norwegian (Nynorsk)', location: 'Norway', tag: 'nn-NO'},
  {
    name: 'Norwegian Bokmål',
    location: 'Svalbard and Jan Mayen',
    tag: 'nb-SJ',
  },
  {name: 'Nuer', location: 'Sudan', tag: 'nus-SD'},
  {name: 'Nuer', location: 'South Sudan', tag: 'nus-SS'},
  {name: 'Nyankole', location: 'Uganda', tag: 'nyn-UG'},
  {name: 'Occitan', location: 'France', tag: 'oc-FR'},
  {name: 'Odia', location: 'India', tag: 'or-IN'},
  {name: 'Oromo', location: 'Ethiopia', tag: 'om-ET'},
  {name: 'Oromo', location: 'Kenya', tag: 'om-KE'},
  {name: 'Pashto', location: 'Afghanistan', tag: 'ps-AF'},
  {name: 'Persian', location: 'Afghanistan', tag: 'fa-AF'},
  {name: 'Persian', location: 'Iran', tag: 'fa-IR'},
  {name: 'Polish', location: 'Poland', tag: 'pl-PL'},
  {name: 'Portuguese', location: 'Angola', tag: 'pt-AO'},
  {name: 'Portuguese', location: 'Brazil', tag: 'pt-BR'},
  {name: 'Portuguese', location: 'Equatorial Guinea', tag: 'pt-GQ'},
  {name: 'Portuguese', location: 'Guinea-Bissau', tag: 'pt-GW'},
  {name: 'Portuguese', location: 'Luxembourg', tag: 'pt-LU'},
  {name: 'Portuguese', location: 'Mozambique', tag: 'pt-MZ'},
  {name: 'Portuguese', location: 'Portugal', tag: 'pt-PT'},
  {name: 'Portuguese', location: 'Switzerland', tag: 'pt-CH'},
  {name: 'Punjabi', location: 'India', tag: 'pa-IN'},
  {name: 'Quechua', location: 'Bolivia', tag: 'quz-BO'},
  {name: 'Quechua', location: 'Ecuador', tag: 'quz-EC'},
  {name: 'Quechua', location: 'Peru', tag: 'quz-PE'},
  {name: 'Ripuarian', location: 'Germany', tag: 'ksh-DE'},
  {name: 'Romanian', location: 'Moldova', tag: 'ro-MD'},
  {name: 'Romanian', location: 'Romania', tag: 'ro-RO'},
  {name: 'Romansh', location: 'Switzerland', tag: 'rm-CH'},
  {name: 'Rombo', location: 'Tanzania', tag: 'rof-TZ'},
  {name: 'Rundi', location: 'Burundi', tag: 'rn-BI'},
  {name: 'Russian', location: 'Belarus', tag: 'ru-BY'},
  {name: 'Russian', location: 'Kazakhstan', tag: 'ru-KZ'},
  {name: 'Russian', location: 'Kyrgyzstan', tag: 'ru-KG'},
  {name: 'Russian', location: 'Moldova', tag: 'ru-MD'},
  {name: 'Russian', location: 'Russia', tag: 'ru-RU'},
  {name: 'Russian', location: 'Ukraine', tag: 'ru-UA'},
  {name: 'Rwa', location: 'Tanzania', tag: 'rwk-TZ'},
  {name: 'Saho', location: 'Eritrea', tag: 'ssy-ER'},
  {name: 'Sakha', location: 'Russia', tag: 'sah-RU'},
  {name: 'Samburu', location: 'Kenya', tag: 'saq-KE'},
  {name: 'Sami (Inari)', location: 'Finland', tag: 'smn-FI'},
  {name: 'Sami (Lule)', location: 'Norway', tag: 'smj-NO'},
  {name: 'Sami (Lule)', location: 'Sweden', tag: 'smj-SE'},
  {name: 'Sami (Northern)', location: 'Finland', tag: 'se-FI'},
  {name: 'Sami (Northern)', location: 'Norway', tag: 'se-NO'},
  {name: 'Sami (Northern)', location: 'Sweden', tag: 'se-SE'},
  {name: 'Sami (Skolt)', location: 'Finland', tag: 'sms-FI'},
  {name: 'Sami (Southern)', location: 'Norway', tag: 'sma-NO'},
  {name: 'Sami (Southern)', location: 'Sweden', tag: 'sma-SE'},
  {name: 'Sango', location: 'Central African Republic', tag: 'sg-CF'},
  {name: 'Sangu', location: 'Tanzania', tag: 'sbp-TZ'},
  {name: 'Sanskrit', location: 'India', tag: 'sa-IN'},
  {name: 'Scottish Gaelic', location: 'United Kingdom', tag: 'gd-GB'},
  {name: 'Sena', location: 'Mozambique', tag: 'seh-MZ'},
  {
    name: 'Serbian (Cyrillic)',
    location: 'Bosnia and Herzegovina',
    tag: 'sr-Cyrl-BA',
  },
  {name: 'Serbian (Cyrillic)', location: 'Montenegro', tag: 'sr-Cyrl-ME'},
  {name: 'Serbian (Cyrillic)', location: 'Serbia', tag: 'sr-Cyrl-RS'},
  {
    name: 'Serbian (Latin)',
    location: 'Bosnia and Herzegovina',
    tag: 'sr-Latn-BA',
  },
  {name: 'Serbian (Latin)', location: 'Montenegro', tag: 'sr-Latn-ME'},
  {name: 'Serbian (Latin)', location: 'Serbia', tag: 'sr-Latn-RS'},
  {name: 'Sesotho sa Leboa', location: 'South Africa', tag: 'nso-ZA'},
  {name: 'Setswana', location: 'Botswana', tag: 'tn-BW'},
  {name: 'Setswana', location: 'South Africa', tag: 'tn-ZA'},
  {name: 'Shambala', location: 'Tanzania', tag: 'ksb-TZ'},
  {name: 'Shona', location: 'Zimbabwe', tag: 'sn-Latn-ZW'},
  {name: 'Sinhala', location: 'Sri Lanka', tag: 'si-LK'},
  {name: 'Slovak', location: 'Slovakia', tag: 'sk-SK'},
  {name: 'Slovenian', location: 'Slovenia', tag: 'sl-SI'},
  {name: 'Soga', location: 'Uganda', tag: 'xog-UG'},
  {name: 'Somali', location: 'Djibouti', tag: 'so-DJ'},
  {name: 'Somali', location: 'Ethiopia', tag: 'so-ET'},
  {name: 'Somali', location: 'Kenya', tag: 'so-KE'},
  {name: 'Somali', location: 'Somalia', tag: 'so-SO'},
  {name: 'Sotho', location: 'South Africa', tag: 'st-ZA'},
  {name: 'South Ndebele', location: 'South Africa', tag: 'nr-ZA'},
  {name: 'Southern Sotho', location: 'Lesotho', tag: 'st-LS'},
  {name: 'Spanish', location: 'Argentina', tag: 'es-AR'},
  {name: 'Spanish', location: 'Belize', tag: 'es-BZ'},
  {name: 'Spanish', location: 'Bolivia', tag: 'es-BO'},
  {name: 'Spanish', location: 'Brazil', tag: 'es-BR'},
  {name: 'Spanish', location: 'Chile', tag: 'es-CL'},
  {name: 'Spanish', location: 'Colombia', tag: 'es-CO'},
  {name: 'Spanish', location: 'Costa Rica', tag: 'es-CR'},
  {name: 'Spanish', location: 'Cuba', tag: 'es-CU'},
  {name: 'Spanish', location: 'Dominican Republic', tag: 'es-DO'},
  {name: 'Spanish', location: 'Ecuador', tag: 'es-EC'},
  {name: 'Spanish', location: 'El Salvador', tag: 'es-SV'},
  {name: 'Spanish', location: 'Equatorial Guinea', tag: 'es-GQ'},
  {name: 'Spanish', location: 'Guatemala', tag: 'es-GT'},
  {name: 'Spanish', location: 'Honduras', tag: 'es-HN'},
  {name: 'Spanish', location: 'Mexico', tag: 'es-MX'},
  {name: 'Spanish', location: 'Nicaragua', tag: 'es-NI'},
  {name: 'Spanish', location: 'Panama', tag: 'es-PA'},
  {name: 'Spanish', location: 'Paraguay', tag: 'es-PY'},
  {name: 'Spanish', location: 'Peru', tag: 'es-PE'},
  {name: 'Spanish', location: 'Philippines', tag: 'es-PH'},
  {name: 'Spanish', location: 'Puerto Rico', tag: 'es-PR'},
  {name: 'Spanish', location: 'Spain', tag: 'es-ES'},
  {name: 'Spanish', location: 'United States', tag: 'es-US'},
  {name: 'Spanish', location: 'Uruguay', tag: 'es-UY'},
  {name: 'Standard Moroccan ', location: 'Morocco', tag: 'zgh-Tfng-MA'},
  {name: 'Swati', location: 'South Africa', tag: 'ss-ZA'},
  {name: 'Swati', location: 'Swaziland', tag: 'ss-SZ'},
  {name: 'Swedish', location: 'Finland', tag: 'sv-FI'},
  {name: 'Swedish', location: 'Sweden', tag: 'sv-SE'},
  {name: 'Syriac', location: 'Syria', tag: 'syr-SY'},
  {name: 'Tachelhit (Latin)', location: 'Morocco', tag: 'shi-Latn-MA'},
  {name: 'Taita', location: 'Kenya', tag: 'dav-KE'},
  {name: 'Tajik (Cyrillic)', location: 'Tajikistan', tag: 'tg-Cyrl-TJ'},
  {name: 'Tamazight (Latin)', location: 'Algeria', tag: 'tzm-Latn-DZ'},
  {name: 'Tamil', location: 'India', tag: 'ta-IN'},
  {name: 'Tamil', location: 'Malaysia', tag: 'ta-MY'},
  {name: 'Tamil', location: 'Singapore', tag: 'ta-SG'},
  {name: 'Tamil', location: 'Sri Lanka', tag: 'ta-LK'},
  {name: 'Tasawaq', location: 'Niger', tag: 'twq-NE'},
  {name: 'Tatar', location: 'Russia', tag: 'tt-RU'},
  {name: 'Telugu', location: 'India', tag: 'te-IN'},
  {name: 'Teso', location: 'Kenya', tag: 'teo-KE'},
  {name: 'Teso', location: 'Uganda', tag: 'teo-UG'},
  {name: 'Thai', location: 'Thailand', tag: 'th-TH'},
  {name: 'Tibetan', location: 'India', tag: 'bo-IN'},
  {name: 'Tigre', location: 'Eritrea', tag: 'tig-ER'},
  {name: 'Tigrinya', location: 'Eritrea', tag: 'ti-ER'},
  {name: 'Tigrinya', location: 'Ethiopia', tag: 'ti-ET'},
  {name: 'Tongan', location: 'Tonga', tag: 'to-TO'},
  {name: 'Tsonga', location: 'South Africa', tag: 'ts-ZA'},
  {name: 'Turkish', location: 'Cyprus', tag: 'tr-CY'},
  {name: 'Turkish', location: 'Turkey', tag: 'tr-TR'},
  {name: 'Turkmen', location: 'Turkmenistan', tag: 'tk-TM'},
  {name: 'Ukrainian', location: 'Ukraine', tag: 'uk-UA'},
  {name: 'Upper Sorbian', location: 'Germany', tag: 'hsb-DE'},
  {name: 'Urdu', location: 'India', tag: 'ur-IN'},
  {name: 'Uzbek (Cyrillic)', location: 'Uzbekistan', tag: 'uz-Cyrl-UZ'},
  {name: 'Uzbek (Latin)', location: 'Uzbekistan', tag: 'uz-Latn-UZ'},
  {name: 'Vai', location: 'Liberia', tag: 'vai-Vaii-LR'},
  {name: 'Vai (Latin)', location: 'Liberia', tag: 'vai-Latn-LR'},
  {name: 'Valencian', location: 'Spain', tag: 'ca-ES'},
  {name: 'Venda', location: 'South Africa', tag: 've-ZA'},
  {name: 'Vietnamese', location: 'Vietnam', tag: 'vi-VN'},
  {name: 'Vunjo', location: 'Tanzania', tag: 'vun-TZ'},
  {name: 'Walser', location: 'Switzerland', tag: 'wae-CH'},
  {name: 'Welsh', location: 'United Kingdom', tag: 'cy-GB'},
  {name: 'Wolaytta', location: 'Ethiopia', tag: 'wal-ET'},
  {name: 'Wolof', location: 'Senegal', tag: 'wo-SN'},
  {name: 'Xhosa', location: 'South Africa', tag: 'xh-ZA'},
  {name: 'Yangben', location: 'Cameroon', tag: 'yav-CM'},
  {name: 'Yoruba', location: 'Benin', tag: 'yo-BJ'},
  {name: 'Yoruba', location: 'Nigeria', tag: 'yo-NG'},
  {name: 'Zarma', location: 'Niger', tag: 'dje-NE'},
  {name: 'Zulu', location: 'South Africa', tag: 'zu-ZA'},
];
